import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import ModalAkun from "./ModalDataAkun";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { ref, onValue, remove, set as dbSet } from "firebase/database";
import { rtdb } from "../../config/firebase";
import toast, { Toaster } from "react-hot-toast";

const roles = [
  { id: "Admin", label: "Admin", icon: ShieldCheckIcon },
  { id: "Pengguna", label: "Pengguna", icon: UserIcon },
];

export default function DataAkun() {
  const [dataAkun, setDataAkun] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const akunRef = ref(rtdb, "akun");
    const unsubscribe = onValue(akunRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const akunList = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setDataAkun(akunList);
      } else {
        setDataAkun([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleAdd = () => {
    setSelectedData(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedData(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const target = dataAkun.find((a) => a.id === id);
    if (!target) return;

    const sameRoleCount = dataAkun.filter((a) => a.role === target.role).length;
    if (sameRoleCount <= 1) {
      toast.error(`Minimal harus ada 1 akun ${target.role} tersisa.`, { icon: "⚠️" });
      return;
    }

    if (!window.confirm(`Yakin ingin menghapus akun ${target.nama}?`)) return;

    try {
      await remove(ref(rtdb, `akun/${id}`));
      await dbSet(ref(rtdb, `toDeleteRequests/${id}`), {
        uid: id,
        requestedAt: new Date().toISOString(),
      });

      toast.success(`Akun ${target.role} (${target.nama}) berhasil dihapus.`, {
        icon: "🗑️",
      });
    } catch (err) {
      console.error(err);
      toast.error("Gagal menghapus akun: " + err.message, { icon: "❌" });
    }
  };

  const handleSave = () => {
    setIsModalOpen(false);
  };

  const groupedAndFilteredData = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const filtered = searchTerm
      ? dataAkun.filter(
          (item) =>
            item.nama?.toLowerCase().includes(searchLower) ||
            item.email?.toLowerCase().includes(searchLower)
        )
      : dataAkun;

    return roles.reduce((acc, role) => {
      acc[role.id] = filtered.filter((d) => d.role === role.id);
      return acc;
    }, {});
  }, [dataAkun, searchTerm]);

  return (
    <div className="w-full min-h-full p-4 md:p-6 bg-slate-50 overflow-visible">
      {/* ✅ FIXED Toaster */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        containerStyle={{
          position: "fixed",
          top: "1rem",
          right: "1rem",
          zIndex: 99999,
        }}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#fff",
            color: "#111",
            border: "1px solid #e2e8f0",
            boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
            borderRadius: "10px",
            fontSize: "14px",
          },
          success: {
            iconTheme: { primary: "#16a34a", secondary: "#fff" },
          },
          error: {
            iconTheme: { primary: "#dc2626", secondary: "#fff" },
          },
        }}
      />

      {/* Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="sticky top-4 z-20 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 mb-6 p-4"
      >
        <div className="flex flex-wrap justify-between items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-800">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-500">
              Manajemen
            </span>{" "}
            Akun Pengguna
          </h2>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Tambah Akun</span>
          </button>
        </div>

        <div className="border-t my-4 border-black/10"></div>
        <div className="flex flex-wrap justify-between items-center gap-4">
          <p className="text-sm text-gray-600">
            Total{" "}
            <span className="font-bold text-gray-800">{dataAkun.length}</span>{" "}
            akun terdaftar
          </p>
          <div className="relative w-full md:w-auto md:max-w-xs">
            <MagnifyingGlassIcon className="absolute w-5 h-5 left-3 top-2.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Cari nama atau email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </motion.header>

      {/* Daftar Akun */}
      <div className="space-y-4">
        {roles.map((role) => {
          const usersInRole = groupedAndFilteredData[role.id] || [];
          const onlyOneLeft = usersInRole.length <= 1;

          return (
            <div key={role.id}>
              <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-md border border-white/50 overflow-hidden p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gradient-to-br from-green-400 to-teal-500 rounded-lg text-white shadow-md">
                      <role.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-800">{role.label}</h3>
                      <span className="text-xs font-semibold text-gray-500">
                        {usersInRole.length} Akun
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  {usersInRole.length > 0 ? (
                    <ul className="divide-y divide-white/50">
                      {usersInRole.map((item) => (
                        <li key={item.id} className="flex items-center justify-between py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-700">
                              {item.nama?.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-gray-900 truncate">{item.nama}</p>
                              <p className="text-xs text-gray-500 truncate">{item.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 rounded-md hover:bg-yellow-100 text-yellow-600"
                            >
                              <PencilSquareIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              disabled={onlyOneLeft}
                              className={`p-2 rounded-md ${
                                onlyOneLeft
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "hover:bg-red-100 text-red-600"
                              }`}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-center text-sm text-gray-500 py-4">
                      Tidak ada hasil ditemukan.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <ModalAkun
          data={selectedData}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
